import React, { createContext, useContext, useReducer } from 'react';

export interface User {
  id: string;
  full_name: string;
  email: string;
  is_pro: boolean;
  niche?: string;
  onboarding_complete: boolean;
  rewardful_token?: string;
  tiktok_handle?: string;
  phone_number?: string;
  experience_level?: string;
  referral_source?: string;
  referred_by_code?: string;
  is_cap_member?: boolean;
  avatar_url?: string;
  niche_categories?: string[] | null;
  creates_in_languages?: string[] | null;
  preferred_content_languages?: string[] | null;
  rate_10_videos_cents?: number | null;
  rate_10_videos_with_rights_cents?: number | null;
  rates_set_at?: string | null;
  roster_creator_id?: string | null;
  creator_gmv?: number | null;
  gender?: string | null;
}

interface AuthState {
  user: User | null;
  token: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;
}

type AuthAction =
  | { type: 'SET_USER'; payload: { user: User; token: string } }
  | { type: 'SIGN_OUT' }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'UPDATE_USER'; payload: Partial<User> };

const initialState: AuthState = {
  user: null,
  token: null,
  isLoading: true,
  isAuthenticated: false,
};

function authReducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case 'SET_USER':
      return { ...state, user: action.payload.user, token: action.payload.token, isAuthenticated: true, isLoading: false };
    case 'SIGN_OUT':
      return { ...state, user: null, token: null, isAuthenticated: false, isLoading: false };
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    case 'UPDATE_USER':
      return { ...state, user: state.user ? { ...state.user, ...action.payload } : null };
    default:
      return state;
  }
}

interface AuthContextType extends AuthState {
  setUser: (user: User, token: string) => void;
  signOut: () => Promise<void>;
  clearSession: () => void;
  updateUser: (updates: Partial<User>) => void;
  setLoading: (loading: boolean) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = useReducer(authReducer, initialState);

  const setUser = (user: User, token: string) => {
    dispatch({ type: 'SET_USER', payload: { user, token } });
  };

  const signOut = async () => {
    const { supabase } = await import('@/lib/supabase');
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    dispatch({ type: 'SIGN_OUT' });
  };

  const updateUser = (updates: Partial<User>) => {
    dispatch({ type: 'UPDATE_USER', payload: updates });
  };

  const setLoading = (loading: boolean) => {
    dispatch({ type: 'SET_LOADING', payload: loading });
  };

  return (
    <AuthContext.Provider value={{ ...state, setUser, signOut, clearSession: () => dispatch({ type: 'SIGN_OUT' }), updateUser, setLoading }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}
